import type { FormEvent, ReactNode } from 'react'
import { Alert } from '../Alert'
import { Button } from '../Button'
import { Card } from '../Card'
import { cn } from '../cn'
import { actionDataAttrs, recipeDataAttrs } from './markers'

export type FormSurfaceProps = {
  title?: ReactNode
  children: ReactNode
  error?: ReactNode
  loading?: boolean
  submitLabel?: ReactNode
  onSubmit?: () => void
  actions?: ReactNode
  className?: string
  /** Optional action id for submit control (default form.submit). */
  submitActionId?: string
  hideSubmit?: boolean
  /** Optional harvest-stable name for the title heading (data-exact-set-name). */
  titleExactName?: string
}

/**
 * Form-1 — policy-free form composition: Card + form region + optional error alert.
 * Apps supply named fields as children (FormField/Input etc.).
 */
export function FormSurface({
  title,
  children,
  error,
  loading = false,
  submitLabel = 'Save',
  onSubmit,
  actions,
  className = '',
  submitActionId = 'form.submit',
  hideSubmit = false,
  titleExactName,
}: FormSurfaceProps) {
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    onSubmit?.()
  }

  return (
    <div {...recipeDataAttrs('Form-1')} className={cn('w-full', className)}>
      <Card className="p-6">
        {title ? (
          <h2 className="m-0 mb-4 font-display text-h3 text-strong" data-exact-set-name={titleExactName}>
            {title}
          </h2>
        ) : null}
        {error ? (
          <Alert variant="error" className="mb-4">
            {error}
          </Alert>
        ) : null}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
          <div data-vjal-form-fields className="flex flex-col gap-4">
            {children}
          </div>
          {actions ??
            (!hideSubmit ? (
              <div className="flex justify-end gap-2">
                <Button
                  type="submit"
                  variant="primary"
                  loading={loading}
                  {...actionDataAttrs(submitActionId)}
                >
                  {submitLabel}
                </Button>
              </div>
            ) : null)}
        </form>
      </Card>
    </div>
  )
}
